import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography, Button, Stack, Tooltip, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * DataViewTable — MUI DataGrid wrapper with title and export controls.
 *
 * Props:
 *   columns        — MUI DataGrid column definition array
 *   rows           — array of row objects (each must have an `id` field)
 *   loading        — bool
 *   total          — total row count (for server-side pagination)
 *   page           — current page (0-indexed for DataGrid)
 *   pageSize       — rows per page
 *   onPageChange   — (newPage: number) => void
 *   onPageSizeChange — (newPageSize: number) => void
 *   title          — string
 *   onExport       — (format: 'csv'|'xlsx') => void
 */
const DataViewTable = ({
  columns = [],
  rows = [],
  loading = false,
  total = 0,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  title = '',
  onExport,
}) => {
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        {title && (
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        )}
        {onExport && (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export CSV">
              <IconButton
                size="small"
                onClick={() => onExport('csv')}
                sx={{ color: '#0057A8' }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => onExport('csv')}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              CSV
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => onExport('xlsx')}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              XLSX
            </Button>
          </Stack>
        )}
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        autoHeight
        rowCount={total}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          if (model.page !== page && onPageChange) {
            onPageChange(model.page);
          }
          if (model.pageSize !== pageSize && onPageSizeChange) {
            onPageSizeChange(model.pageSize);
          }
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f0f4ff',
          },
        }}
      />
    </Box>
  );
};

export default DataViewTable;
